import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import Formsy from 'formsy-react';
import { Typography, Grid, Button, InputLabel, Chip, CircularProgress, InputAdornment, SnackbarContent } from '@mui/material';
import TextInput from 'components/inputs/TextInput';
import CheckboxInput from 'components/inputs/CheckboxInput';
import RadioInput from 'components/inputs/RadioInput';
import DateInput from 'components/inputs/DateInput';
import moment from 'moment';
import { fetchProduct } from 'actions/ProductActions';
import { addToCart } from 'actions/CartActions';
import { currencyCode, currencySign, minMobileNumberLength, maxMobileNumberLength, mobileNumberLengthRange } from 'utils/locales';
import { purchaseDisplay } from 'utils/promotions';
import renderHTML from 'html-react-parser';
import ProductPanel from 'components/ProductPanel';
import Expandable from 'components/Expandable';
import InfoIcon from '@mui/icons-material/Info';
import Autolinker from 'autolinker';
import { viewContent } from 'utils/tracking';
import { appendSeoBrand } from 'utils/seo';
import { FormattedMessage, FormattedNumber, FormattedDate, injectIntl } from 'react-intl';

const presetValues = [50, 100, 200];
const promotionStyle = {
  color: window.PROMOTION_FONT_COLOUR,
  background: window.PROMOTION_BACKGROUND_COLOUR,
  padding: '0.75em'
};

export class OrderCardForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      value: null,
      cardType: null,
      includeMessage: false,
      sendNow: '1',
      whoFor: '1',
      deliveryMethod: 'email'
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.updateAmount = this.updateAmount.bind(this);
  }

  updateAmount(evt) {
    const { product } = this.props;
    const value = parseFloat(evt.target.value);

    if (value >= product.min_amount && value <= product.max_amount) {
      this.setState({ value: value });
    }
  }

  componentDidMount() {
    this.loadCardDesign();
  }

  componentDidUpdate(prevProps) {
    const { product, match, locale } = this.props;

    // To handle case where navigating from one product to another, which doesn't remount but changes the ID in the URL
    // Also handles when locale is changed and new translations need to be pulled
    if (match.params.productSlug != prevProps.match.params.productSlug || locale !== prevProps.locale) {
      this.loadCardDesign();
    }

    if (product && product !== prevProps.product) {
      // TODO: Can we tidy this a bit?
      var defaultValue = product.product_type == 'digital_retailer' && product.denominations.length == 1 ? product.denominations[0] : (product.default_value || product.min_amount);
      var type = product.product_type == 'physical' ? 'physical' : 'digital';
      var deliveryMethod = 'email';
      switch (product.product_type) {
        case 'physical':
          deliveryMethod = 'post';
          break;
        case 'digital_retailer':
          deliveryMethod = 'email';
          break;
        case 'digital':
          deliveryMethod = 'sms';
          break;
      }

      this.setState({
        value: parseFloat(defaultValue).toFixed(defaultValue % 1 === 0 ? 0 : 2),
        cardType: type,
        deliveryMethod: deliveryMethod
      });
      viewContent(product.name);
    }
  }

  loadCardDesign() {
    const { site, match, locale, fetchProduct } = this.props;

    var slug = match.params.productSlug;
    fetchProduct(site.id, slug, locale);
  }

  productDenominations(product) {
    if (product.product_type == 'digital_retailer' && product.denominations.length > 0) {
      return product.denominations.map(d => {return Number(d)}).sort((x,y)=> x-y);
    } else if (product.preset_denominations?.length > 0) {
      return product.preset_denominations;
    } else if (product.product_type !== 'digital_retailer') {
      return presetValues;
    }

    return [];
  }

  handleSubmit(data, _resetForm, _invalidateForm) {
    const { deliveryMethod } = this.state;
    const { product, addToCart, site } = this.props;

    addToCart(_.merge(data, {site_id: site.id, card_design_id: product.id,
      promotion_item_id: product.promotion?.item_id, delivery_method: deliveryMethod}));
  }

  render() {
    const { site, product, classes, isFetching, max_card_quantity, digital_card_send_hour, isProcessing, navigateTo, intl } = this.props;
    const { cardType, sendNow, whoFor, deliveryMethod } = this.state;

    if (isFetching || !product) {
      return <center><CircularProgress color="primary" size={50} /></center>
    }

    const minCardValue = product.min_amount;
    const maxCardValue = product.max_amount;
    const denominations = this.productDenominations(product);
    const requiredValidation= { isDefaultRequiredValue: intl.formatMessage({id: "validation_required", defaultMessage: "can't be blank"})};

    return (
      <React.Fragment>
        <Helmet>
          <title>{appendSeoBrand(product.page_title)}</title>
          <meta name="description" content={product.description ? product.description.substring(0, 150) : `Purchase a ${product.name} gift card today`} />
          <meta name="robots" content={window.APP_ENV === 'production' ? 'index' : 'noindex'} />
          <link rel="canonical" href={window.location.href} />
        </Helmet>
        <section className="single-product-container grid-outer">
          <article className="single-product grid medium">
            <div className="product-info">
              <div className="product-thumb">
                <img src={product.image} alt={product.name} />
              </div>

              <div className="product-description">
                <Typography variant="h5" component="h1" className="top-space bottom-space">{product.name}</Typography>
                {product.description &&
                  <Typography component="span" color="textPrimary" paragraph>
                    {renderHTML(product.description)}
                  </Typography>
                }
              </div>
              {site.product_tags_enabled &&
                <div className="tag-container">
                  {product.tags.map((tag, index) =>
                    <Chip key={index} clickable label={tag.name} variant="outlined" onClick={() => navigateTo(`/tags/${tag.slug}`)} />
                  )}
                </div>
              }
            </div>

            <div className="product-form">
              <Formsy onValidSubmit={this.handleSubmit} style={{width: '100%', marginBottom: '2em', marginTop: '8px'}}>
                <Grid container spacing={1} justify='center'>
                  <Grid size={{xs: 12}}>
                    <TextInput type="hidden" name="card_type" value={cardType} style={{display: 'none'}} />

                    {denominations.length > 1 &&
                      <React.Fragment>
                        <InputLabel component="div"><FormattedMessage id="order.gift_card_value" defaultMessage="Gift card value" /></InputLabel>
                        {product.fee > 0 &&
                          <Typography paragraph>
                            <FormattedNumber value={parseFloat(product.fee)} style='currency' currency={currencyCode()} currencyDisplay='narrowSymbol'/>
                            <FormattedMessage id="order.card_fee" defaultMessage=" (inc {gst}) fee per card applies" values={{gst: 'GST'}}/>
                          </Typography>
                        }
                        {denominations.map((value, index) =>
                          <Button key={index} variant='outlined' color="primary" onClick={() => this.setState({value: value})} style={{margin: '0.5em 5px'}}>
                            <FormattedNumber value={value} style='currency' currency={currencyCode()} currencyDisplay='narrowSymbol' maximumFractionDigits={value % 1 === 0 ? '0' : '2'} />
                          </Button>
                        )}
                      </React.Fragment>
                    }
                    <TextInput type='number' name='amount'
                               label={
                                product.product_type != 'digital_retailer' ?
                                  intl.formatMessage({id: "order.enter_amount", defaultMessage: "Enter Your Own Amount"})
                                :
                                  intl.formatMessage({id: product.preset_denominations?.length > 0 ? "order.or_enter_amount" : "order.amount", defaultMessage: "Amount"})
                               }
                               required
                               value={this.state.value}
                               disabled={product.product_type == 'digital_retailer' && !product.has_range}
                               hint={
                                product.product_type != 'digital_retailer' ? '' :
                                product.has_range
                                ? intl.formatMessage({id: "order.enter_amount_between", defaultMessage: "Enter an amount between {min} and {max}" }, { min: currencySign()+parseFloat(minCardValue), max: currencySign()+parseFloat(maxCardValue) })
                                : denominations.length > 1
                                ? intl.formatMessage({id: "order.select_amount", defaultMessage: "Select an amount above"}) : null
                               }
                               onChange={this.updateAmount}
                      inputProps={{
                        startAdornment: <InputAdornment position="start">{currencySign()}</InputAdornment>
                      }}
                      inputFieldProps={{
                        step: '0.01'
                      }}
                      validations={{
                        isGreaterEqualTo: minCardValue,
                        isLessEqualTo: maxCardValue
                      }}
                      validationErrors={Object.assign({}, requiredValidation, {
                        isGreaterEqualTo: intl.formatMessage({id: "order.amount_min", defaultMessage: "must be greater than or equal to"}) + ' ' + currencySign() + parseInt(minCardValue),
                        isLessEqualTo: intl.formatMessage({id: "order.amount_max", defaultMessage: "must be less than or equal to"}) + ' ' + currencySign() + parseInt(maxCardValue)
                      })}
                    />
                    {product.promotion &&
                      <div style={{display: 'flex'}}>
                        <div style={window.PROMOTION_BACKGROUND_COLOUR.length > 1 ? promotionStyle : {}} >
                          <Typography variant="caption" style={{color: window.PROMOTION_FONT_COLOUR}}>
                            {purchaseDisplay(product.promotion, this.state.value, intl)}
                          </Typography>
                          {product.promotion.promotion_type == 'buy_one_get_one_free' &&
                            <React.Fragment>
                              <TextInput name="free_product_id" type="hidden" value={product.promotion.free_card_design.id} />
                              <TextInput name="free_product_value" type="hidden" value={product.promotion.value} />
                            </React.Fragment>
                          }
                        </div>
                      </div>
                    }

                    {(product.product_type == 'physical' || product.product_type == 'digital_retailer') &&
                      <TextInput type='number' name='quantity'
                                 label={intl.formatMessage({id: "order.quantity", defaultMessage: "Quantity"})}
                                 value='1'
                                 required
                        validations={{
                          isGreaterThan: 0,
                          isLessEqualTo: max_card_quantity
                        }}
                        validationErrors={Object.assign({}, requiredValidation, {
                          isGreaterThan: intl.formatMessage({id: "order.quantity_min", defaultMessage: "must be greater than 0"}),
                          isLessEqualTo: intl.formatMessage({id: "order.quantity_max", defaultMessage: "maximum number of cards is"}) + ' ' + max_card_quantity
                        })}
                      />
                    }

                    {(product.product_type == 'physical' || product.product_type == 'digital_retailer') &&
                      <React.Fragment>
                        <RadioInput name='who_for'
                                    label={intl.formatMessage({id: "order.who_for", defaultMessage: "Who is the card for?"})}
                                    row
                                    value={whoFor}
                                    onChange={(value) => this.setState({whoFor: value})}
                                    options={[
                                      {label: intl.formatMessage({id: "order.who_for.myself", defaultMessage: "Myself"}), value: '0'},
                                      {label: intl.formatMessage({id: "order.who_for.someone_else", defaultMessage: "Someone else"}), value: '1'}]}
                        />
                      </React.Fragment>
                    }

                    {(product.product_type == 'digital_retailer') && (this.props.allow_digital_retailer_sms_delivery) &&
                      <React.Fragment>
                        <RadioInput name='delivery_method'
                                    label={intl.formatMessage({id: "order.how_to_deliver", defaultMessage: "How would you like it to be delivered?"})}
                                    row
                                    value={deliveryMethod}
                                    onChange={(value) => this.setState({deliveryMethod: value})}
                                    options={[
                                      {label: intl.formatMessage({id: "order.how_to_deliver.email", defaultMessage: "Email"}), value: 'email'},
                                      {label: intl.formatMessage({id: "order.how_to_deliver.sms", defaultMessage: "SMS"}), value: 'sms'}]}
                        />
                      </React.Fragment>
                    }

                    {(whoFor == '0' && product.product_type == 'digital_retailer') &&
                      <Typography>
                        {(deliveryMethod == 'email') &&
                          <FormattedMessage id="order.who_for.myself_email" defaultMessage="Card will be delivered to the billing email address"/>
                        }
                        {(deliveryMethod == 'sms') &&
                          <FormattedMessage id="order.who_for.myself_sms" defaultMessage="Card will be delivered to the billing mobile number"/>
                        }
                      </Typography>
                    }
                    {(whoFor == '1') &&
                      <React.Fragment>
                        {(deliveryMethod == 'sms') &&
                          <React.Fragment>
                            <TextInput type='number' name='delivery_sms_number'
                                       label={intl.formatMessage({id: "order.delivery_sms", defaultMessage: "Recipient Mobile Number"})}
                                       required
                              hint={product.product_type == 'digital' ? intl.formatMessage({id: "order.delivery_sms_hint", defaultMessage: "Mobile number the digital card will be sent to (Australian numbers only). The card cannot be forwarded to another number after delivery"}) : ''}
                              validations={{minLength: minMobileNumberLength(), maxLength: maxMobileNumberLength()}}
                              validationError={intl.formatMessage({id: "order.delivery_sms_length", defaultMessage: "must be {length} digits"}, { length: mobileNumberLengthRange() })}
                              validationErrors={requiredValidation}
                            />

                            {(product.product_type == 'digital') &&
                              <SnackbarContent
                                className="info-container"
                                aria-describedby="info-snackbar"
                                message={
                                  <span id="info-snackbar" className="message">
                                    <InfoIcon className="icon" />
                                    <FormattedMessage id="order.delivery_sms_info" defaultMessage="Please Note: The card cannot be forwarded to another phone number after delivery" />
                                  </span>
                                }
                              />
                            }

                            <TextInput type='number' name='delivery_sms_number_confirmation'
                                       label={intl.formatMessage({id: "order.delivery_sms_confirmation", defaultMessage: "Recipient Mobile Number Confirmation"})}
                                       required
                              validations={{minLength: minMobileNumberLength(), maxLength: maxMobileNumberLength(), equalsField: `delivery_sms_number`}}
                              validationError={intl.formatMessage({id: "order.delivery_sms_length", defaultMessage: "must be {length} digits"}, { length: mobileNumberLengthRange() })}
                              validationErrors={_.merge(requiredValidation, {equalsField: intl.formatMessage({id: "order.delivery_sms_mismatch", defaultMessage: "must match recipient number"})})}
                            />
                            {(product.product_type == 'digital') &&
                              <Grid container spacing={2}>
                                <Grid size={{xs: 12, md: 6}}>
                                  <TextInput name="delivery_first_name"
                                             label={intl.formatMessage({id: "order.delivery_first_name", defaultMessage: "Recipient First Name"})}
                                             required
                                    validationErrors={requiredValidation}
                                  />
                                </Grid>
                                <Grid size={{xs: 12, md: 6}}>
                                  <TextInput name="delivery_last_name"
                                             label={intl.formatMessage({id: "order.delivery_last_name", defaultMessage: "Recipient Last Name"})}
                                             required
                                    validationErrors={requiredValidation}
                                  />
                                </Grid>
                              </Grid>
                            }
                          </React.Fragment>
                        }

                        {(deliveryMethod == 'email') &&
                          <React.Fragment>
                            <TextInput name="delivery_email" required
                              label={intl.formatMessage({id: "order.delivery_email", defaultMessage: "Recipient Email"})}
                              hint={intl.formatMessage({id: "order.delivery_email_hint", defaultMessage: "Email address the digital card will be sent to."})}
                                         delivery_email_hint
                              validations="isEmail"
                              validationErrors={_.merge(requiredValidation, {isEmail: intl.formatMessage({id: "order.delivery_email_invalid", defaultMessage: "must be a valid email address"})})}
                            />

                            <TextInput name="delivery_email_confirmation" required
                              label={intl.formatMessage({id: "order.delivery_email_confirmation", defaultMessage: "Recipient Email Confirmation"})}
                              validations="isEmail,equalsField:delivery_email"
                              validationErrors={_.merge(requiredValidation, {isEmail: intl.formatMessage({id: "order.delivery_email_invalid", defaultMessage: "must be a valid email address"}),
                                                                             equalsField: intl.formatMessage({id: "checkout.details.email_mismatch", defaultMessage: "Must match email"})
                              })}
                            />
                          </React.Fragment>
                        }

                        {(product.product_type == 'digital' || product.product_type == 'digital_retailer') &&
                          <React.Fragment>
                            <RadioInput name='send_now' label='' row
                              value={sendNow}
                              onChange={(value) => this.setState({sendNow: value})}
                              options={[
                                {label: intl.formatMessage({id: "order.send_now", defaultMessage: "Send Now"}), value: '1'},
                                {label: intl.formatMessage({id: "order.send_later", defaultMessage: "Send Later"}), value: '0'}]}
                            />

                            {sendNow == '0' &&
                              <DateInput name="scheduled_time"
                                         label={intl.formatMessage({id: "order.scheduled_date", defaultMessage: "Scheduled Date"})}
                                         scheduled_date
                                         minDate={moment().add(1, 'days')} maxDate={moment().add(3, 'months').calendar()}
                                         hint={intl.formatMessage({id: "order.scheduled_date_hint",
                                           defaultMessage: "Date to send the card, up to 3 months from now. Cards will be sent at {time} on the date selected." },
                                           {time: moment().hour(digital_card_send_hour).format('ha')})}
                                         required />
                            }
                          </React.Fragment>
                        }

                        <CheckboxInput name="include_message"
                                       label={intl.formatMessage({id: "order.include_message", defaultMessage: "Would you like to include a message?"})}
                          onChange={(v) => this.setState({includeMessage: v === '1'})}
                        />

                        {this.state.includeMessage &&
                          <React.Fragment>
                            {(product.product_type == 'physical' || product.product_type == 'digital_retailer') &&
                              <TextInput name='to'
                                         label={intl.formatMessage({id: "order.message_to", defaultMessage: "To"})}
                                         required
                                validationErrors={requiredValidation}
                              />
                            }
                            <TextInput name='from'
                                       label={intl.formatMessage({id: "order.message_from", defaultMessage: "From"})}
                                         message_from
                                       required
                              validationErrors={requiredValidation}
                            />
                            <TextInput name='message'
                                       label={intl.formatMessage({id: "order.message", defaultMessage: "Message"})}
                                       multiline required
                              hint={intl.formatMessage({id: "order.message_hint", defaultMessage: "Inappropriate messages will not be included"})}
                              validationErrors={requiredValidation}
                            />
                          </React.Fragment>
                        }
                      </React.Fragment>
                    }
                  </Grid>
                </Grid>
                <div className='button-container'>
                  <Button type='submit' variant='contained' color='primary' disabled={isProcessing}>
                    <FormattedMessage id="order.add_to_cart" defaultMessage="Add to cart"/>
                  </Button>
                </div>
              </Formsy>
            </div>

            {product.related_products?.length > 0 &&
              <ProductPanel products={product.related_products} headingKey="order.related_products" headingFallback="You might also like" />
            }

            {(product.terms_and_conditions || (product.promotion && product.promotion.end_at)) &&
              <div className='terms-and-conditions' style={{marginTop: '2em', marginBottom: '1em'}}>
                <Typography variant='body1' gutterBottom>
                  <strong>
                    <FormattedMessage id="order.terms" defaultMessage="Terms and Conditions" />
                  </strong>
                </Typography>
                <Typography component="span" color="textSecondary" style={{marginTop: '1em', marginBottom: '2em'}}>
                  {renderHTML(Autolinker.link(product.terms_and_conditions))}
                </Typography>
                {product.promotion && product.promotion.end_at &&
                  <Typography component="span" color="textSecondary" style={{marginTop: '1em', marginBottom: '2em'}}>
                    *
                    <FormattedMessage id="order.offer_ends" defaultMessage="Promotion ends on {date}"
                                      values={{date: new Date(product.promotion.end_at).toLocaleDateString('en-au', {year:"numeric", month:"long", day:"numeric"})}}/>
                  </Typography>
                }
              </div>
            }
            {product.redemption_instructions &&
              <Expandable title={intl.formatMessage({id: "order.redemption_instructions", defaultMessage: "Redemption Instructions"})}>
                <Typography component="span" color="textSecondary" style={{marginTop: '1em', marginBottom: '1em'}}>
                  {renderHTML(Autolinker.link(product.redemption_instructions))}
                </Typography>
              </Expandable>
            }
          </article>
        </section>

      </React.Fragment>
    )
  }
}

function mapStateToProps(state) {
  return {
    isFetching: state.product.isFetching,
    product: state.product.product,
    site: state.site.site,
    ...state.site.config,
    locale: state.locale.locale,
    isProcessing: state.cart.isProcessing
  };
}

const mapDispatchToProps = {
  fetchProduct: (siteId, productSlug, locale) => fetchProduct(siteId, productSlug, locale),
  addToCart: (data) => addToCart(data),
  navigateTo: (path) => push(path)
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(OrderCardForm));
